/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useServiceContext } from '../../../../root'
import { useRcmTableData } from '../../../../hooks/useRcmTableData'
import { TaskStatus, TaskType } from '../../../../constants/task'
import { WorkflowType, WorkflowStepType, TASK_ACTIONS } from '../../../../constants/workflow'
import { AclLevel, FsiType } from '../../../../constants/fsi'
import { RcmTable } from '../../../rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell } from '../../../table/TableComponents'
import ActionsMenu from '../../../shared/ActionsMenu'
import TextLinkButton from '../../../shared/TextLinkButton'
import TaskDocLink from '../../../shared/TaskDocLink'
import { Link, useNavigate } from 'react-router'
import UploadFileRequestModal from '~/pages/browser/upload-file/UploadFileRequestModal'
import { useModal } from '~/contexts/ModalContext'
import WorkflowDetailsModal from '~/components/modals/WorkflowDetailsModal'
import WorkflowCommentsModal from '~/components/modals/WorkflowCommentsModal'
import TaskCompleteModal from '~/screens/tasks/Actions/complete/TaskCompleteModal'
import TaskCancel from '~/screens/tasks/Actions/cancel/TaskCancel'
import TaskDetailsModal from '~/screens/tasks/Actions/details/TaskDetailsModal'
import { FileDiffModal } from '~/components/modals/FileDiffModal'
import { FsiPropertiesModal } from '~/components/modals/FsiPropertiesModal'
import { toast } from '~/services/toast'
import { VersionsModal } from '~/components/modals/VersionsModal'
import { TaskManageDocsModal } from '~/screens/tasks/Actions/manage-docs'
import { getFileDownloadUrl } from '~/services/api/files'
import FsiPickerModal from '~/components/modals/FsiPickerModal'
import { WorkflowEditModal } from '~/components/modals/WorkflowEditModal'
import { WorkflowCollaboratorsModal } from '~/components/modals/WorkflowCollaboratorsModal'
import { WorkflowReviewModal } from '~/components/modals/WorkflowReviewModal'
import { WorkflowQcApproversModal } from '~/components/modals/WorkflowQcApproversModal'
import { WorkflowApproversModal } from '~/components/modals/WorkflowApproversModal'
import { WorkflowTraineesModal } from '~/components/modals/WorkflowTraineesModal'
import { WorkflowRejectionReviewersModal } from '~/components/modals/WorkflowRejectionReviewersModal'
import { useNextTask } from '~/services/nextTask'
import TasksTable from '~/screens/tasks/TasksTable'

interface MyTasksTableProps {
  isEosEnabled: boolean
}

interface Task {
  plain(): Task
  id: number
  name: string
  status: string
  taskType?: string
  workflowType?: string
  workflowStepType?: string
  workflowDescription?: string
  workflowId?: number
  workflowDraftFolderId?: string
  startDate?: number
  assignee?: {
    id: number
    name: string
  }
  workflow?: {
    id: number
    formattedId: string
  }
  studyName?: string
  siteName?: string
  document?: {
    name: string
  }
  files?: FileInfo[]
  flags?: TaskFlags
  canEditWorkflow?: boolean
  canAddUserToNOfAllTaskGroup?: boolean
  canEditWorkflowReviewers?: boolean
  canEditWorkflowApprovers?: boolean
  canEditWorkflowTrainees?: boolean
  canEditRejectionReviewers?: boolean
}

interface FileInfo {
  draftFsi?: FileSystemItem
  sourceFsi?: FileSystemItem
  targetFsi?: FileSystemItem
}

interface FileSystemItem {
  id: string
  name: string
  type: string
  versionId?: string
  minorVersionId?: string
  aclLevel?: string
}

interface TaskFlags {
  canRevert?: boolean
  canCancel?: boolean
  canReassign?: boolean
  canApprove?: boolean
  canCancelRATask?: boolean
  canReject?: boolean
  canViewComments?: boolean
  canChangeProperties?: boolean
  multifileWorkflow?: boolean
  canDownloadFile?: boolean
  canUploadFile?: boolean
  canUploadFromLocalPC?: boolean
  canUploadFromGDrive?: boolean
  canUploadFromOneDrive?: boolean
}

interface MenuItem {
  value?: string
  action?: () => void
  submenu?: MenuItem[]
  divider?: boolean
  disabled?: boolean
  label?: string
}

// TableMenu Component - Comprehensive version based on JSX
const TableMenu: React.FC<{ item: Task; options?: { hideDetails?: boolean } }> = ({ item, options = {} }) => {
  const context = useServiceContext()
  const {
    api,
    service: {
      filter: { translate: t }
    }
  } = context
  const { showModal } = useModal()
  const navigate = useNavigate()
  const { checkNextTask } = useNextTask()
  const [optionsLoaded, setOptionsLoaded] = useState(false)
  const [currentTaskForUpload, setCurrentTaskForUpload] = useState<Task | null>(null)
  const [isFsiPickerOpen, setIsFsiPickerOpen] = useState(false)
  const [items, setItems] = useState<MenuItem[]>([
    {
      value: 'LOADING...',
      action: () => {},
      disabled: true
    }
  ])

  const getCompleteTaskOptions = (task: Task, hideAction?: string): MenuItem[] => {
    const menu: MenuItem[] = []

    if (task.taskType === TaskType.USER_REJECTED_TASK) {
      if (task.flags?.canRevert) {
        menu.push({
          value: 'ACTION_COMMAND_REVERT_CR_TO_EDIT_REVIEW',
          action: () => {
            // Get the file to view (prioritize draftFsi, then targetFsi, then sourceFsi)
            const file = task.files?.[0]
              ? task.files[0].draftFsi
                ? task.files[0].draftFsi
                : task.files[0].targetFsi
                  ? task.files[0].targetFsi
                  : task.files[0].sourceFsi
              : null

            // Navigate to show-file if there's a viewable file
            if (file && file.type !== FsiType.PLACEHOLDER) {
              navigate(
                `show-file/${file.id}?action=taskComplete&taskId=${task.id}&versionId=${file.versionId || -1}&minorVersionId=${file.minorVersionId || 0}`
              )
            } else {
              // Show modal if no file or placeholder
              showModal(<TaskCompleteModal task={task} type={TASK_ACTIONS.taskComplete} />)
            }
          }
        })
      }

      if (task.flags?.canCancel) {
        menu.push({
          value: 'ACTION_COMMAND_CANCEL_CR',
          action: () => {
            showModal(
              <TaskCancel
                task={task}
                onFinished={() => {
                  // Task cancelled successfully
                }}
              />
            )
          }
        })
      }

      if (task.flags?.canReassign) {
        menu.push({
          value: 'TASKS_TASK_ACTIONS_REASSIGN_TASK',
          action: () => {
            showModal(<TaskCompleteModal task={task} type={TASK_ACTIONS.taskReassign} />)
          }
        })
      }
    }
    // USER task type
    else if (task.taskType === TaskType.USER) {
      if (task.flags?.canApprove) {
        menu.push({
          value: 'TASKS_TASK_ACTIONS_APPROVE_TASK',
          action: () => {
            // Get the file to view (prioritize draftFsi, then targetFsi, then sourceFsi)
            const file = task.files?.[0]
              ? task.files[0].draftFsi
                ? task.files[0].draftFsi
                : task.files[0].targetFsi
                  ? task.files[0].targetFsi
                  : task.files[0].sourceFsi
              : null

            // For QC_REVIEW workflows, we need to get the first file from workflow files
            if (task.workflowType === WorkflowType.QC_REVIEW) {
              // Load workflow files and navigate to first file with action
              api.workflows
                .getWorkflowFiles(task.workflowId || 0, { pageStart: 0, pageSize: 1 })
                .then((resp) => {
                  const firstFile = resp?.elements?.[0]?.draftFsi
                  if (firstFile) {
                    // For QC workflows, we'd navigate to showFileQC route
                    // For now, use regular show-file route
                    navigate(
                      `show-file-qc/${firstFile.id}?action=taskComplete&taskId=${task.id}&versionId=${(firstFile as any).versionId || -1}&minorVersionId=${(firstFile as any).minorVersionId || 0}`
                    )
                  }
                })
                .catch(() => {
                  // Fallback to modal if API fails
                  showModal(
                    <TaskCompleteModal task={task} type={TASK_ACTIONS.taskComplete} onFinished={checkNextTask} />
                  )
                })
            } else if (
              file &&
              file.type !== FsiType.PLACEHOLDER &&
              task.workflowStepType !== WorkflowStepType.DOWNLOAD_EOS
            ) {
              // Navigate to show-file for regular workflows with viewable files
              navigate(
                `show-file/${file.id}?action=taskComplete&taskId=${task.id}&versionId=${file.versionId || -1}&minorVersionId=${file.minorVersionId || 0}`
              )
            } else {
              // Show modal if no file, placeholder, or DOWNLOAD_EOS step
              showModal(<TaskCompleteModal task={task} type={TASK_ACTIONS.taskComplete} onFinished={checkNextTask} />)
            }
          }
        })
      }

      if (task.flags?.canCancelRATask) {
        menu.push({
          value: 'ACTION_COMMAND_CANCEL_TASK',
          action: () => {
            showModal(
              <TaskCancel
                task={task}
                isRATask={true}
                onFinished={() => {
                  // RA Task cancelled successfully
                }}
              />
            )
          }
        })
      }

      // Not TRAINING type tasks
      if (task.workflowStepType !== WorkflowStepType.TRAINING) {
        if (task.flags?.canReject) {
          menu.push({
            value: 'TASKS_TASK_ACTIONS_REJECT_TASK',
            action: () => {
              // Get the file to view (prioritize draftFsi, then targetFsi, then sourceFsi)
              const file = task.files?.[0]
                ? task.files[0].draftFsi
                  ? task.files[0].draftFsi
                  : task.files[0].targetFsi
                    ? task.files[0].targetFsi
                    : task.files[0].sourceFsi
                : null

              // Navigate to show-file if there's a viewable file
              if (file && file.type !== FsiType.PLACEHOLDER) {
                navigate(
                  `show-file/${file.id}?action=taskReject&taskId=${task.id}&versionId=${file.versionId || ''}&minorVersionId=${file.minorVersionId || 0}`
                )
              } else {
                // Show modal if no file or placeholder
                showModal(<TaskCompleteModal task={task} type={TASK_ACTIONS.taskReject} />)
              }
            }
          })
        }

        if (task.flags?.canReassign) {
          menu.push({
            value: 'TASKS_TASK_ACTIONS_REASSIGN_TASK',
            action: () => {
              showModal(<TaskCompleteModal task={task} type={TASK_ACTIONS.taskReassign} />)
            }
          })
        }
      }
    }

    return menu
  }

  const handleDocumentSelect = async (fsi: any) => {
    if (!fsi || !fsi.id || !currentTaskForUpload) {
      setIsFsiPickerOpen(false)
      setCurrentTaskForUpload(null)
      return
    }

    try {
      const folderId = currentTaskForUpload.workflowDraftFolderId
      if (!folderId) {
        toast.error(t('NO_FOLDER_SPECIFIED'))
        return
      }

      // Call API to upload version from existing document
      await api.files.uploadVersionFromExistingDocument(fsi.id, Number(folderId))
      toast.success(t('BROWSER_UPLOAD_FILE_SUCCESS_MSG'))
    } catch (error: any) {
      console.error('Upload from RCM failed:', error)
      toast.error(error.message || t('UPLOAD_FAILED'))
    } finally {
      setIsFsiPickerOpen(false)
      setCurrentTaskForUpload(null)
    }
  }

  const buildLowerMenu = (task: Task): MenuItem[] => {
    const lowerMenu: MenuItem[] = []

    if (task.taskType === TaskType.USER_REJECTED_TASK) {
      if (!options.hideDetails) {
        lowerMenu.push({
          value: 'BROWSER_ACTION_VIEW_TASK_DETAILS',
          action: () => {
            showModal(<TaskDetailsModal taskId={task.id} onClose={() => {}} />)
          }
        })
      }

      if (task.flags?.canViewComments) {
        lowerMenu.push({
          value: 'ACTION_COMMAND_VIEW',
          submenu: [
            {
              value: 'VIEW_COMMENTS_ACTION',
              action: () => {
                showModal(<WorkflowCommentsModal workflow={task} />)
              }
            }
          ]
        })
      }

      if (!task.flags?.multifileWorkflow && task.files?.[0]) {
        const file =
          task.files[0].draftFsi &&
          task.files[0].draftFsi.type !== FsiType.PLACEHOLDER &&
          ![AclLevel.NONE, AclLevel.BROWSE].includes(task.files[0].draftFsi.aclLevel as any)
            ? task.files[0].draftFsi
            : task.files[0].sourceFsi &&
                task.files[0].sourceFsi.type !== FsiType.PLACEHOLDER &&
                ![AclLevel.NONE, AclLevel.BROWSE].includes(task.files[0].sourceFsi.aclLevel as any)
              ? task.files[0].sourceFsi
              : null

        if (file) {
          lowerMenu.push({
            value: 'BROWSER_ACTION_VERSIONS',
            action: () => {
              showModal(<VersionsModal fsiId={file.id} />)
            }
          })
        }
      }
    } else if (task.taskType === TaskType.USER) {
      if (![WorkflowStepType.TRAINING].includes(task.workflowStepType as any)) {
        if (!options.hideDetails) {
          const menuTarget = ![WorkflowStepType.DOWNLOAD_EOS].includes(task.workflowStepType as any)
            ? lowerMenu
            : lowerMenu

          menuTarget.push({
            value: 'BROWSER_ACTION_VIEW_TASK_DETAILS',
            action: () => {
              showModal(<TaskDetailsModal taskId={task.id} onClose={() => {}} />)
            }
          })
        }

        // Build view submenu
        const subViewMenu: MenuItem[] = []

        if (
          task.workflowType !== WorkflowType.IMPORT &&
          task.workflowType !== WorkflowType.QC_REVIEW &&
          task.files?.length === 1 &&
          task.files[0].sourceFsi?.id &&
          task.files[0].sourceFsi.type !== FsiType.PLACEHOLDER
        ) {
          subViewMenu.push({
            value: 'ACTION_COMMAND_VIEW_DOCUMENT_CURRENT',
            action: () => {
              const fileId = task.files?.[0]?.sourceFsi?.id
              if (fileId) {
                navigate(`show-file/${fileId}`)
              }
            }
          })

          if (
            task.workflowType !== WorkflowType.IMPORT &&
            task.workflowStepType !== WorkflowStepType.DOWNLOAD_EOS &&
            task.files.length === 1 &&
            task.files[0].draftFsi?.id &&
            task.files[0].draftFsi.type !== FsiType.PLACEHOLDER
          ) {
            subViewMenu.push({
              value: 'ACTION_COMMAND_VIEW_DOCUMENT_DRAFT',
              action: () => {
                const fileId = task.files?.[0]?.draftFsi?.id
                if (fileId) {
                  navigate(`show-file/${fileId}`)
                }
              }
            })
          }
        }

        // Compare action
        if (
          task.workflowType === WorkflowType.CHANGE &&
          [WorkflowStepType.REVISE_EDIT, WorkflowStepType.REVIEW, WorkflowStepType.APPROVE].includes(
            task.workflowStepType as any
          ) &&
          task.files &&
          task.files.length > 0 &&
          task.files[0].draftFsi?.id &&
          task.files[0].draftFsi.type !== FsiType.PLACEHOLDER
        ) {
          subViewMenu.push({
            value: 'ACTION_COMMAND_COMPARE',
            action: () => {
              const fileId = task.files?.[0]?.draftFsi?.id
              if (fileId) {
                showModal(<FileDiffModal fileId={fileId} onClose={() => {}} />)
              }
            }
          })
        }

        if (task.flags?.canViewComments && task.workflowType !== WorkflowType.QC_REVIEW) {
          subViewMenu.push({
            value: 'VIEW_COMMENTS_ACTION',
            action: () => {
              showModal(<WorkflowCommentsModal workflow={task} />)
            }
          })
        }

        if (subViewMenu.length > 0) {
          lowerMenu.push({ value: 'ACTION_COMMAND_VIEW', submenu: subViewMenu })
        }

        // Additional menu items
        if (!task.flags?.multifileWorkflow && task.flags?.canChangeProperties && task.files?.length) {
          lowerMenu.push({
            value: 'BROWSER_ACTION_PROPERTIES',
            action: () => {
              const fileId = task.files?.[0]?.draftFsi?.id
              if (fileId) {
                showModal(
                  <FsiPropertiesModal
                    fsiId={fileId}
                    onSuccess={() => {
                      toast.success(t('PROPERTIES_UPDATED_SUCCESS_MSG'))
                    }}
                    onClose={() => {}}
                  />
                )
              }
            }
          })
        }

        // Versions action
        if (!task.flags?.multifileWorkflow && task.files?.[0]) {
          const file =
            task.files[0].draftFsi &&
            task.files[0].draftFsi.type !== FsiType.PLACEHOLDER &&
            ![AclLevel.NONE, AclLevel.BROWSE].includes(task.files[0].draftFsi.aclLevel as any)
              ? task.files[0].draftFsi
              : task.files[0].sourceFsi &&
                  task.files[0].sourceFsi.type !== FsiType.PLACEHOLDER &&
                  ![AclLevel.NONE, AclLevel.BROWSE].includes(task.files[0].sourceFsi.aclLevel as any)
                ? task.files[0].sourceFsi
                : null

          if (
            file &&
            ![
              WorkflowStepType.ACKNOWLEDGE_EOS,
              WorkflowStepType.DOWNLOAD_EOS,
              WorkflowStepType.REVIEW_QC,
              WorkflowStepType.APPROVE_QC
            ].includes(task.workflowStepType as any)
          ) {
            lowerMenu.push({
              value: 'BROWSER_ACTION_VERSIONS',
              action: () => {
                showModal(<VersionsModal fsiId={String(file.id)} />)
              }
            })
          }
        }

        // Manage Documents for multifile workflows
        if (task.flags?.multifileWorkflow) {
          lowerMenu.push({
            value: 'ACTION_COMMAND_COMPARE_MULTI',
            action: () => {
              showModal(<TaskManageDocsModal task={task} />)
            }
          })
        }

        // Download and Upload actions
        if (task.flags?.canDownloadFile) {
          lowerMenu.push({
            value: 'ACTION_COMMAND_DOWNLOAD_DOCUMENT',
            action: async () => {
              const draftFsi = task.files?.[0]?.draftFsi
              if (!draftFsi) {
                toast.info(t('NO_FILES_TO_BE_DOWNLOADED'))
                return
              }

              try {
                toast.info(t('BROWSER_DOWNLOAD_IS_PREPARING'))
                const downloadInfo = await getFileDownloadUrl(Number(draftFsi.id))
                if (downloadInfo?.message) {
                  toast.info(downloadInfo.message)
                }
                if (downloadInfo?.url) {
                  window.open(downloadInfo.url, '_blank')
                } else {
                  toast.error(t('DOWNLOAD_FAILED'))
                }
              } catch (error) {
                console.error('Download error:', error)
                toast.error(t('DOWNLOAD_FAILED'))
              }
            }
          })
        }

        // Upload actions
        if (task.flags?.canUploadFile) {
          const subMenuUpload: MenuItem[] = []

          if (task.flags.canUploadFromLocalPC) {
            subMenuUpload.push({
              value: 'FROM_MY_COMPUTER',
              action: () => {
                const folderId =
                  typeof task.workflowDraftFolderId === 'number'
                    ? task.workflowDraftFolderId
                    : typeof task.workflowDraftFolderId === 'string' && !isNaN(Number(task.workflowDraftFolderId))
                      ? Number(task.workflowDraftFolderId)
                      : undefined
                if (typeof folderId === 'number') {
                  showModal(
                    <UploadFileRequestModal
                      parentId={folderId}
                      params={{
                        workflowId: task?.workflowId ? task?.workflowId : 0,
                        task: task as import('~/services/api/types').Task
                      }}
                      onSuccess={() => {}}
                      onFailure={() => {}}
                    />
                  )
                }
              }
            })

            subMenuUpload.push({
              value: 'FROM_RCM',
              action: () => {
                setCurrentTaskForUpload((prev) => task)
                setIsFsiPickerOpen(true)
              }
            })
          }

          if (task.flags.canUploadFromGDrive) {
            subMenuUpload.push({
              value: 'FROM_GOOGLE_DRIVE',
              action: async () => {
                const fileId = task.files?.[0]?.draftFsi?.id
                if (!fileId) {
                  toast.error(t('NO_FILE_TO_UPLOAD'))
                  return
                }
                try {
                  await api.files.uploadFromGoogleDrive(Number(fileId))
                  toast.success(t('DOC_UPLOADED_MSG'))
                } catch (error: any) {
                  toast.error(error.message || t('UPLOAD_FAILED'))
                }
              }
            })
          }

          if (task.flags.canUploadFromOneDrive) {
            subMenuUpload.push({
              value: 'DOWNLOAD_FROM_OFFICE_365',
              action: async () => {
                const fileId = task.files?.[0]?.draftFsi?.id
                if (!fileId) {
                  toast.error(t('NO_FILE_TO_UPLOAD'))
                  return
                }
                try {
                  await api.files.uploadFromOneDrive(Number(fileId))
                  toast.success(t('BROWSER_UPLOAD_FILE_O365_SUCCESS_MSG'))
                } catch (error: any) {
                  toast.error(error.message || t('UPLOAD_FAILED'))
                }
              }
            })
          }

          if (subMenuUpload.length > 0) {
            lowerMenu.push({ value: 'ACTION_COMMAND_UPLOAD_DOCUMENT', submenu: subMenuUpload })
          }

          // Edit in external apps
          const isExistingDoc = task.files?.[0]?.draftFsi
          if (isExistingDoc && task.flags?.canUploadFromGDrive) {
            const fileExtension = task.files?.[0]?.draftFsi?.name?.split('.').pop()?.toLowerCase()
            let editAction = null

            if (['doc', 'docx', 'txt'].includes(fileExtension || '')) {
              editAction = 'EDIT_IN_GOOGLE_DOCS_ACTION'
            } else if (['csv', 'xls', 'xlsx'].includes(fileExtension || '')) {
              editAction = 'EDIT_IN_GOOGLE_SHEETS_ACTION'
            }

            if (editAction) {
              lowerMenu.push({
                value: editAction,
                action: async () => {
                  const fileId = task.files?.[0]?.draftFsi?.id
                  if (!fileId) {
                    toast.error(t('NO_FILE_TO_EDIT'))
                    return
                  }
                  try {
                    await api.files.editInGoogleDocs(Number(fileId))
                  } catch (error: any) {
                    toast.error(error.message || t('EDIT_FAILED'))
                  }
                }
              })
            }
          }

          // Edit in Office 365
          if (isExistingDoc && task.flags?.canUploadFromOneDrive) {
            lowerMenu.push({
              value: 'EDIT_IN_OFFICE_365_ACTION',
              action: async () => {
                const fileId = task.files?.[0]?.draftFsi?.id
                const fileExtension = task.files?.[0]?.draftFsi?.name?.split('.').pop()?.toLowerCase()
                if (!fileId) {
                  toast.error(t('NO_FILE_TO_EDIT'))
                  return
                }
                try {
                  await api.files.editInOffice365(Number(fileId), fileExtension)
                } catch (error: any) {
                  if (error.message === 'EDIT_IN_OFFICE365_DOC_NOT_SUPPORTED') {
                    toast.error(t('EDIT_IN_OFFICE365_DOC_NOT_SUPPORTED'))
                  } else {
                    toast.error(error.message || t('EDIT_FAILED'))
                  }
                }
              }
            })
          }
        }
      }
    }

    return lowerMenu
  }

  const buildManageCRMenu = (task: Task): MenuItem[] => {
    const subMenu: MenuItem[] = []

    if (task.canEditWorkflow) {
      subMenu.push({
        value: 'EDIT',
        action: () => {
          showModal(<WorkflowEditModal workflowId={task.workflowId || 0} workflow={task.workflow} />)
        }
      })
    }

    if (task.canAddUserToNOfAllTaskGroup) {
      subMenu.push({
        value: 'CR_ACTION_ADD_COLLABORATORS_TO_CURRENT_PHASE',
        action: () => {
          showModal(<WorkflowCollaboratorsModal workflowId={Number(task.workflowId)} workflow={task.workflow} />)
        }
      })
    }

    if (task.canEditWorkflowReviewers) {
      subMenu.push({
        value: 'BROWSER_ACTION_REVIEWERS',
        action: () => {
          showModal(<WorkflowReviewModal workflowId={Number(task.workflowId)} workflow={task.workflow} />)
        }
      })
    }

    if (task.canEditWorkflowApprovers) {
      subMenu.push({
        value: task.workflowType === WorkflowType.QC_REVIEW ? 'WF_PHASE_QC_APPROVAL' : 'BROWSER_ACTION_APPROVERS',
        action: () => {
          const isQcReview = task.workflowType === WorkflowType.QC_REVIEW
          if (isQcReview) {
            showModal(<WorkflowQcApproversModal workflowId={Number(task.workflowId)} workflow={task.workflow} />)
          } else {
            showModal(<WorkflowApproversModal workflowId={Number(task.workflowId)} workflow={task.workflow} />)
          }
        }
      })
    }

    if (task.canEditWorkflowTrainees) {
      subMenu.push({
        value: 'BROWSER_ACTION_TRAINEES',
        action: () => {
          showModal(<WorkflowTraineesModal workflowId={Number(task.workflowId)} workflow={task.workflow} />, {
            keepParentModalsOpen: true
          })
        }
      })
    }

    if (task.canEditRejectionReviewers) {
      subMenu.push({
        value: 'BROWSER_ACTION_REJECTION_REVIEWERS',
        action: () => {
          showModal(<WorkflowRejectionReviewersModal workflowId={Number(task.workflowId)} workflow={task.workflow} />)
        }
      })
    }

    return subMenu
  }

  const setOptions = (task: Task) => {
    const taskOptions = getCompleteTaskOptions(task)
    const menu = [...taskOptions]
    const lowerMenu = buildLowerMenu(task)

    if (lowerMenu.length) {
      // divider
      if (menu.length) {
        menu.push({ divider: true })
      }

      menu.push(...lowerMenu)
    }

    // MANAGE CR MENU
    const manageCRMenu = buildManageCRMenu(task)
    if (manageCRMenu.length > 0) {
      menu.push({ value: 'BROWSER_ACTION_MANAGE_CR', submenu: manageCRMenu })
    }

    setItems(menu)
  }

  const handleMenuToggle = (isOpen: boolean) => {
    if (isOpen && !optionsLoaded) {
      api.tasks
        .getTask(item.id.toString())
        .then((data: any) => {
          setOptions(data)
          setOptionsLoaded(true)
        })
        .catch((error: any) => {
          console.error('Failed to load task options:', error)
          setOptionsLoaded(true)
        })
    }
  }

  return (
    <>
      <ActionsMenu items={items} menuId={`task-${item.id}`} onToggle={handleMenuToggle} isBigMenu={false} />
      {isFsiPickerOpen && (
        <div style={{ textAlign: 'left' }}>
          <FsiPickerModal
            options={{
              selector: 'file',
              showRC: true,
              showSystem: false
            }}
            title={t('ACTION_COMMAND_UPLOAD_DOCUMENT')}
            isOpen={isFsiPickerOpen}
            onClose={() => {
              setIsFsiPickerOpen(false)
              setCurrentTaskForUpload(null)
            }}
            onSelect={handleDocumentSelect}
          />
        </div>
      )}
    </>
  )
}

export const MyTasksTable: React.FC<MyTasksTableProps> = ({ isEosEnabled }) => {
  const {
    service: {
      filter: { translate }
    }
  } = useServiceContext()
  const tableData = useRcmTableData()
  const { showModal } = useModal()

  useEffect(() => {
    // Load data using context
    tableData.refreshTable()
    const handleRefresh = () => tableData.refreshTable()
    window.addEventListener('updateDashboard', handleRefresh)

    // Cleanup
    return () => {
      window.removeEventListener('updateDashboard', handleRefresh)
    }
  }, []) // Empty dependency array to run only on mount

  if (isEosEnabled) {
    return (
      <TasksTable
        viewType={'USER' as any}
        filterShown={false}
        isEosEnabled={isEosEnabled}
        hidePaginate={true}
        TableFooter={
          <tr>
            <td colSpan={7} className="view-all">
              <Link to="/tasks/me">
                {translate('VIEW_ALL')} ({tableData.total > 500 ? '500+' : tableData.total})
              </Link>
            </td>
          </tr>
        }
      />
    )
  }

  return (
    <RcmTable
      className="tasks-list-table chita-table dashboard-table"
      noResultsText="HURRAY_NO_TASKS"
      hidePaginate={true}
      disableOnInitLoad={false}
      TableFooter={
        <tr>
          <td colSpan={4} className="view-all">
            <Link to="/tasks/me">
              {translate('VIEW_ALL')} ({tableData.total > 500 ? '500+' : tableData.total})
            </Link>
          </td>
        </tr>
      }
      HeadRow={
        <TableHeadRow>
          <TableHeadCell id="01ccc9b5-9949-48f4-ab4f-183b650ee209">
            {translate('TASKS_TABLE_HEADER_NAME')}
          </TableHeadCell>
          <TableHeadCell id="eef9d930-1cab-40a6-9b33-7bdc93697880">{translate('CHANGE_REQUEST')}</TableHeadCell>
          <TableHeadCell id="6199da55-80d4-4da7-84b7-2f9cdeb32535">{translate('DOCUMENT_NAME')}</TableHeadCell>
          <TableHeadCell id="cbb94c1d-caeb-49fe-830b-e253e6680e44" className="actions">
            {translate('TASKS_TABLE_HEADER_ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={({ row }: { row: Task }) => (
        <tr key={row.id}>
          <td>{row.name}</td>
          <td>
            {row.workflow && (
              <TextLinkButton
                onClick={() => {
                  showModal(<WorkflowDetailsModal wfId={row.workflow?.id || 0} />)
                }}
              >
                {row.workflow.formattedId}
              </TextLinkButton>
            )}
          </td>
          <td className="doc-name clipped-cell">
            <TaskDocLink task={row} />
          </td>
          <td className="actions">{row.status === TaskStatus.OPEN && <TableMenu item={row} />}</td>
        </tr>
      )}
    />
  )
}
